import { vec3, mat4 } from 'gl-matrix';
import { Image3D } from '../../domain/patient/image3d';
import { Grid } from '../../engine/data/common/Grid';
import { VoiBounds } from '~/state/atoms/toolsAtoms';
import { resampleInt16Grid } from '~/domain/services/resampleGrid';

/**
 * Compares two Grid objects for equality within specified tolerances.
 * TODO: probably made into a method of Grid class?
 *
 * @param gridA - first Grid to compare
 * @param gridB - second Grid to compare
 * @param resolutionTolerance - tolerance for resolution comparison (default: [1e-6, 1e-6, 1e-6])
 * @param rotationTolerance - tolerance for rotation part of transformation matrix (default: 1e-6)
 * @param offsetTolerance - tolerance for offset part of transformation matrix (default: 1e-6)
 * @returns true if grids are equal within tolerances, false otherwise
 */
export function gridsAreEqual(
    gridA: Grid,
    gridB: Grid,
    resolutionTolerance: vec3 = vec3.fromValues(1e-6, 1e-6, 1e-6),
    rotationTolerance: number = 1e-6,
    offsetTolerance: number = 1e-6
): boolean {
    const sizeA = gridA.getSize();
    const sizeB = gridB.getSize();
    const resA = gridA.getRes();
    const resB = gridB.getRes();

    // Check sizes and resolutions
    for (let i = 0; i < 3; i++) {
        if (sizeA[i] !== sizeB[i]) {
            return false;
        }
        if (Math.abs(resA[i] - resB[i]) > resolutionTolerance[i]) {
            return false;
        }
    }

    const transA = gridA.getTransformation();
    const invTransB = mat4.invert(mat4.create(), gridB.getTransformation());

    const transformsComposed = mat4.multiply(mat4.create(), invTransB!, transA);

    const transformDiff = mat4.subtract(
        mat4.create(),
        transformsComposed,
        mat4.create()
    );

    // Check rotation part
    for (let i = 0; i < 3; i++) {
        for (let j = 0; j < 3; j++) {
            if (Math.abs(transformDiff[i * 4 + j]) > rotationTolerance) {
                return false;
            }
        }
    }

    // Check offset part
    for (let i = 0; i < 3; i++) {
        if (Math.abs(transformDiff[i * 4 + 3]) > offsetTolerance) {
            return false;
        }
    }

    return true;
}

export class Image3DArray {
    public pixelData: Int16Array;
    public grid: Grid;
    public meta: Map<string, string>;

    constructor(pixelData: Int16Array, grid: Grid, meta: Map<string, string>) {
        this.pixelData = pixelData;
        this.grid = grid;
        this.meta = meta;
    }

    static fromImage3D(image: Image3D): Image3DArray {
        return new Image3DArray(
            image.pixelData,
            image.grid,
            new Map<string, string>([
                ['seriesInstanceUid', image.dicomUid],
                ['frameOfReferenceUid', image.frameOfReferenceUid],
                ['modality', image.firstSlice.modality],
            ])
        );
    }

    resample(targetGrid: Grid, transform: mat4 = mat4.create()): Image3DArray {
        const resampledPixels = resampleInt16Grid(
            this.pixelData,
            this.grid,
            targetGrid,
            {
                patientSrcFromDst: mat4.invert(mat4.create(), transform),
            }
        );
        return new Image3DArray(
            resampledPixels.pixels,
            targetGrid,
            new Map(this.meta)
        );
    }

    resampleFast(
        targetGrid: Grid,
        transform: mat4 = mat4.create()
    ): Image3DArray {
        const resampledPixels = resampleInt16Grid(
            this.pixelData,
            this.grid,
            targetGrid,
            {
                fastAverageInsteadOfTrilinear: true,
                patientSrcFromDst: mat4.invert(mat4.create(), transform),
            }
        );
        return new Image3DArray(
            resampledPixels.pixels,
            targetGrid,
            new Map(this.meta)
        );
    }

    checkEmpty(throwError: boolean = true): boolean {
        if (this.pixelData.length === 0) {
            if (throwError) {
                throw new Error('Image3DArray has empty pixel data');
            } else {
                return true;
            }
        }
        return false;
    }

    min(): number {
        this.checkEmpty();
        let min = this.pixelData[0];
        for (let i = 1; i < this.pixelData.length; i++) {
            const v = this.pixelData[i];
            if (v < min) {
                min = v;
            }
        }
        return min;
    }

    max(): number {
        this.checkEmpty();
        let max = this.pixelData[0];
        for (let i = 1; i < this.pixelData.length; i++) {
            const v = this.pixelData[i];
            if (v > max) {
                max = v;
            }
        }
        return max;
    }

    median(): number {
        this.checkEmpty();
        const sorted = Array.from(this.pixelData).sort((a, b) => a - b);
        const mid = Math.floor(sorted.length / 2);
        if (sorted.length % 2 === 0) {
            return (sorted[mid - 1] + sorted[mid]) / 2;
        } else {
            return sorted[mid];
        }
    }

    sum(): number {
        this.checkEmpty();
        let total = 0;
        for (let i = 0; i < this.pixelData.length; i++) {
            total += this.pixelData[i];
        }
        return total;
    }

    mean(): number {
        return this.sum() / this.pixelData.length;
    }

    /**
     * Counts the number of voxels with value >= thresh.
     * If thresh is null, uses the median value as threshold.
     *
     * @param thresh - optional threshold value
     * @returns
     */
    volume(thresh: number | null = null): number {
        this.checkEmpty();
        let count = 0;

        let threshToUse: number;
        if (thresh === null) {
            threshToUse = this.mean();
        } else {
            threshToUse = thresh;
        }

        for (let i = 0; i < this.pixelData.length; i++) {
            if (this.pixelData[i] >= threshToUse) {
                count++;
            }
        }

        return count * this.grid.resX * this.grid.resY * this.grid.resZ;
    }

    /**
     * Returns a new Image3DArray with absolute pixel values.
     *
     * @returns new Image3DArray with absolute pixel values
     */

    abs(): Image3DArray {
        this.checkEmpty();
        const absPixelData = new Int16Array(this.pixelData.length);
        for (let i = 0; i < this.pixelData.length; i++) {
            absPixelData[i] = Math.abs(this.pixelData[i]);
        }
        return new Image3DArray(absPixelData, this.grid, this.meta);
    }

    /**
     * Subtracts another Image3DArray from this one.
     * Can also be used via the subtraction operator if TypeScript supported operator overloading.
     *
     * @param other - Image3DArray to subtract from this one
     * @param resolutionTolerance - tolerance for resolution comparison
     * @param rotationTolerance - tolerance for rotation part of transformation matrix
     * @param offsetTolerance - tolerance for offset part of transformation matrix
     * @returns new Image3DArray containing the difference
     */
    subtract(
        other: Image3DArray,
        resolutionTolerance: vec3 = vec3.fromValues(1e-6, 1e-6, 1e-6),
        rotationTolerance: number = 1e-6,
        offsetTolerance: number = 1e-6
    ): Image3DArray {
        this.checkEmpty();
        other.checkEmpty();

        if (
            !gridsAreEqual(
                this.grid,
                other.grid,
                resolutionTolerance,
                rotationTolerance,
                offsetTolerance
            )
        ) {
            throw new Error(
                'Cannot compute difference of Image3DArrays with different grids'
            );
        }

        const diffPixelData = new Int16Array(this.pixelData.length);
        for (let i = 0; i < this.pixelData.length; i++) {
            diffPixelData[i] = this.pixelData[i] - other.pixelData[i];
        }

        return new Image3DArray(diffPixelData, this.grid, this.meta);
    }
}

/**
 * Crops an Image3DArray to the specified VOI bounds.
 *
 * @param image - image to crop
 * @param bounds - voi bounds in world coordinates
 * @returns - cropped Image3DArray
 */
export function cropImage3DArray(
    image: Image3DArray,
    bounds: VoiBounds
): Image3DArray {
    const minVec = vec3.fromValues(bounds.minX, bounds.minY, bounds.minZ);
    const maxVec = vec3.fromValues(bounds.maxX, bounds.maxY, bounds.maxZ);

    const minVoxel = vec3.floor(vec3.create(), image.grid.toVoxelSpace(minVec));
    const maxVoxel = vec3.ceil(vec3.create(), image.grid.toVoxelSpace(maxVec));

    const grid_size = vec3.fromValues(
        image.grid.sizeX,
        image.grid.sizeY,
        image.grid.sizeZ
    );

    // Clamp to grid bounds
    vec3.max(minVoxel, minVoxel, vec3.fromValues(0, 0, 0));
    vec3.min(minVoxel, minVoxel, grid_size);
    vec3.max(maxVoxel, maxVoxel, vec3.fromValues(0, 0, 0));
    vec3.min(maxVoxel, maxVoxel, grid_size);

    const croppedSizeX = maxVoxel[0] - minVoxel[0];
    const croppedSizeY = maxVoxel[1] - minVoxel[1];
    const croppedSizeZ = maxVoxel[2] - minVoxel[2];

    const croppedPixelData = new Int16Array(
        croppedSizeX * croppedSizeY * croppedSizeZ
    );

    for (let z = 0; z < croppedSizeZ; z++) {
        for (let y = 0; y < croppedSizeY; y++) {
            for (let x = 0; x < croppedSizeX; x++) {
                const srcX = minVoxel[0] + x;
                const srcY = minVoxel[1] + y;
                const srcZ = minVoxel[2] + z;

                const srcIndex =
                    srcZ * image.grid.sizeX * image.grid.sizeY +
                    srcY * image.grid.sizeX +
                    srcX;
                const dstIndex =
                    z * croppedSizeX * croppedSizeY + y * croppedSizeX + x;

                croppedPixelData[dstIndex] = image.pixelData[srcIndex];
            }
        }
    }

    const cropped_transformation = mat4.clone(image.grid.getTransformation());
    const newOrigin = image.grid.toWorldSpace(minVoxel);
    cropped_transformation[12] = newOrigin[0];
    cropped_transformation[13] = newOrigin[1];
    cropped_transformation[14] = newOrigin[2];

    return new Image3DArray(
        croppedPixelData,
        new Grid(
            croppedSizeX,
            croppedSizeY,
            croppedSizeZ,
            image.grid.resX,
            image.grid.resY,
            image.grid.resZ,
            cropped_transformation
        ),
        image.meta
    );
}
